import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Loader2, ZoomIn, ZoomOut, AlertCircle, RefreshCw } from 'lucide-react';

const BASE_PAGE_WIDTH = 760;

/**
 * In-app PDF viewer — displays server-rasterized page images of the ACTUAL
 * generated PDF (same bytes Download produces). No client-side PDF engine,
 * so it renders identically on every browser including iPad Safari.
 * States: loading (generating), error (with retry), pages.
 * Shared by the Sales Report page and the Schedule Report modal.
 */
const PdfPreviewModal = ({ open, onOpenChange, title, loading, error, pages, onRetry }) => {
    const { t } = useTranslation();
    const [zoom, setZoom] = useState(1);
    const pageList = pages || [];

    useEffect(() => {
        if (open) setZoom(1);
    }, [open]);

    const pageWidth = Math.min(BASE_PAGE_WIDTH, (typeof window !== 'undefined' ? window.innerWidth : BASE_PAGE_WIDTH) - 80) * zoom;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-[96vw] sm:max-w-4xl lg:max-w-5xl h-[92vh] flex flex-col p-0 gap-0"
                data-testid="pdf-preview-modal"
            >
                <DialogHeader className="px-4 py-2.5 border-b shrink-0">
                    <div className="flex items-center justify-between gap-3 pr-8">
                        <DialogTitle className="text-base truncate" data-testid="pdf-preview-title">{title}</DialogTitle>
                        {pageList.length > 0 && (
                            <div className="flex items-center gap-1.5 shrink-0">
                                <Button
                                    type="button" size="icon" variant="outline" className="h-7 w-7"
                                    onClick={() => setZoom(z => Math.max(0.5, Math.round((z - 0.15) * 100) / 100))}
                                    data-testid="pdf-preview-zoom-out"
                                >
                                    <ZoomOut className="w-3.5 h-3.5" />
                                </Button>
                                <span className="text-xs text-muted-foreground w-11 text-center tabular-nums" data-testid="pdf-preview-zoom-level">
                                    {Math.round(zoom * 100)}%
                                </span>
                                <Button
                                    type="button" size="icon" variant="outline" className="h-7 w-7"
                                    onClick={() => setZoom(z => Math.min(2.5, Math.round((z + 0.15) * 100) / 100))}
                                    data-testid="pdf-preview-zoom-in"
                                >
                                    <ZoomIn className="w-3.5 h-3.5" />
                                </Button>
                                <span className="text-xs text-muted-foreground ml-2" data-testid="pdf-preview-page-count">
                                    {pageList.length} p.
                                </span>
                            </div>
                        )}
                    </div>
                    <DialogDescription className="sr-only">{title}</DialogDescription>
                </DialogHeader>
                <div className="flex-1 min-h-0 overflow-auto bg-slate-200 dark:bg-slate-900" data-testid="pdf-preview-scroll">
                    {loading && (
                        <div className="flex flex-col items-center justify-center h-full gap-3" data-testid="pdf-preview-loading">
                            <Loader2 className="w-7 h-7 animate-spin text-muted-foreground" />
                            <p className="text-sm text-muted-foreground">{t('salesReport.generatingPdf')}</p>
                        </div>
                    )}
                    {!loading && error && (
                        <div className="flex flex-col items-center justify-center h-full gap-3 px-6 text-center" data-testid="pdf-preview-error">
                            <AlertCircle className="w-8 h-8 text-red-500" />
                            <p className="text-sm text-foreground font-medium">{t('salesReport.pdfPreview.failed')}</p>
                            <div className="flex gap-2 mt-1">
                                {onRetry && (
                                    <Button type="button" variant="default" size="sm" onClick={onRetry} data-testid="pdf-preview-retry-btn">
                                        <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                                        {t('salesReport.pdfPreview.tryAgain')}
                                    </Button>
                                )}
                                <Button type="button" variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="pdf-preview-close-btn">
                                    {t('salesReport.pdfPreview.close')}
                                </Button>
                            </div>
                        </div>
                    )}
                    {!loading && !error && pageList.length > 0 && (
                        <div className="flex flex-col items-center gap-4 py-4 px-2">
                            {pageList.map((src, i) => (
                                <img
                                    key={i}
                                    src={src}
                                    alt={`${title} — ${i + 1}`}
                                    className="shadow-lg bg-white"
                                    style={{ width: `${pageWidth}px`, maxWidth: 'none' }}
                                    data-testid={`pdf-preview-page-${i + 1}`}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default PdfPreviewModal;
